/** LocalStorage 工具函数 */
import { isServer } from "solid-js/web";

// iOS 兼容性检测
const isIOS = () => {
  if (isServer) return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// 多重存储策略
const getStorageFallback = (key: string): string | null => {
  try {
    // 1. 尝试从 localStorage 获取
    const localValue = localStorage.getItem(key);
    if (localValue) return localValue;

    // 2. 尝试从 cookie 获取
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === key) return value;
    }

    return null;
  } catch (error) {
    console.error("Error in storage fallback:", error);
    return null;
  }
};

const setStorageFallback = (key: string, value: string): void => {
  try {
    // 1. 优先存储到 localStorage
    localStorage.setItem(key, value);

    // 2. 对于重要数据，也存储到 cookie
    if (key === "Token" || key === "user_id") {
      document.cookie = `${key}=${value}; path=/; max-age=2592000`; // 30天
    }
  } catch (error) {
    console.error("Error in storage fallback set:", error);
  }
};

/**
 * 设置 localStorage 项
 *
 * @param key 键名
 * @param value 值
 */
export const setLocalStorageItem = <T>(key: string, value: T): void => {
  try {
    const serializedValue =
      value instanceof Object ? JSON.stringify(value) : String(value);

    // 使用多重存储策略
    setStorageFallback(key, serializedValue);
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
};

/**
 * 获取 localStorage 项
 *
 * @param key 键名
 * @param defaultValue 默认值
 * @returns 存储的值或默认值
 */
export const getLocalStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    if (!isServer) {
      // 使用多重存储策略获取
      const item = getStorageFallback(key);
      if (item === null) return defaultValue;

      try {
        return JSON.parse(item);
      } catch {
        // 如果不是有效的JSON，直接返回字符串值
        return item as unknown as T;
      }
    }
    return defaultValue;
  } catch (error) {
    console.error("Error reading from localStorage:", error);
    return defaultValue;
  }
};

/**
 * 删除 localStorage 项
 *
 * @param key 键名
 */
export const removeLocalStorageItem = (key: string): void => {
  try {
    if (!isServer) {
      // 删除所有存储位置的数据
      localStorage.removeItem(key);
      // 删除 cookie
      document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    }
  } catch (error) {
    console.error("Error removing from localStorage:", error);
  }
};

/** 清空所有 localStorage 项 */
export const clearLocalStorage = (): void => {
  try {
    if (!isServer) {
      // 清空所有存储位置
      localStorage.clear();
      // 清空所有 cookies
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      }
    }
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }
};

/**
 * 获取所有 localStorage 键名
 *
 * @returns 键名数组
 */
export const getAllLocalStorageKeys = (): string[] => {
  try {
    const keys = new Set<string>();

    // 合并所有存储位置的键
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keys.add(key);
    }

    return Array.from(keys);
  } catch (error) {
    console.error("Error getting localStorage keys:", error);
    return [];
  }
};
